<template>
  <v-dialog fullscreen hide-overlay transition="dialog-bottom-transition" v-model="detailReportDialog">
    <v-app-bar class="detail-report-bar" elevation="0">
      <v-btn class="close-button" icon @click="$emit('close')" aria-label="Close employee report">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <v-toolbar-title class="report-title">
        <span class="report-eyebrow">Employee report</span>
        <strong class="text-capitalize">{{ empData.name }}</strong>
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn class="report-action" @click="print()" rounded small outlined>
        <v-icon left small>mdi-printer-outline</v-icon>
        <span class="action-label">Print</span>
      </v-btn>
      <Create :appbar="true" v-if="employeeAccess && employeeAccess.timeClock.create" @submit="dataSave"
        @error="dataError" :employee="empData" />
    </v-app-bar>
    <v-card class="detail-report-screen" tile>
      <v-progress-linear v-if="loading" class="report-loader" indeterminate color="primary"></v-progress-linear>
      <v-container class="detail-report-container">
        <template v-if="empData">
          <section class="employee-overview">
            <div class="employee-heading">
              <div class="employee-avatar">
                {{ empData.name ? empData.name.charAt(0).toUpperCase() : "E" }}
              </div>
              <div>
                <div class="section-eyebrow">Time card</div>
                <h1 class="employee-name text-capitalize">{{ empData.name }}</h1>
                <div class="employee-meta">
                  <span v-if="empData.phoneNumber"><v-icon small>mdi-phone-outline</v-icon>{{ empData.phoneNumber }}</span>
                  <span><v-icon small>mdi-calendar-range</v-icon>{{ dateTime.start | moment("MMM D") }} – {{ dateTime.end | moment("MMM D, YYYY") }}</span>
                </div>
              </div>
            </div>

            <div class="metric-grid">
              <div v-if="employeeAccess && employeeAccess.report.showPay" class="metric-card">
                <span class="metric-label">Pay rate</span>
                <strong v-if="empData.metadata.timeClockLogin && empData.metadata.timeClockLogin.fullDayEmployee">
                  {{ (empData.metadata.timeClockLogin.fullDayRate || 0) | currency }}
                  <small>/ day</small>
                </strong>
                <strong v-else>{{ empData.hourlyRate | currency }} <small>/ hour</small></strong>
              </div>
              <div class="metric-card">
                <span class="metric-label">Daily target</span>
                <strong>{{ workingHours }} <small>hours</small></strong>
              </div>
              <div v-if="employeeAccess && employeeAccess.report.showHours" class="metric-card">
                <span class="metric-label">Hours worked</span>
                <strong>{{ secondsToHms(totalSeconds) }}</strong>
              </div>
              <div class="metric-card">
                <span class="metric-label">Expected hours</span>
                <strong>{{ secondsToHms(workingSeconds()) }}</strong>
              </div>
            </div>

            <div :class="['variance-card', diffTime() > 0 ? 'is-short' : 'is-over']">
              <v-icon>{{ diffTime() > 0 ? "mdi-alert-circle-outline" : "mdi-check-circle-outline" }}</v-icon>
              <div>
                <span>{{ diffTime() > 0 ? "Below expected hours" : "Above expected hours" }}</span>
                <strong>{{ secondsToHms(Math.abs(diffTime())) }}</strong>
              </div>
            </div>
          </section>

          <section v-if="employeeAccess && employeeAccess.report.showPay" class="pay-overview">
            <div class="pay-heading">
              <div>
                <div class="section-eyebrow">Estimated earnings</div>
                <h2>Pay summary</h2>
              </div>
              <v-chip v-if="empData.payOverTime" class="overtime-chip" small>
                <v-icon left small>mdi-timer-outline</v-icon>Overtime active
              </v-chip>
            </div>
            <div v-if="empData.payOverTime" class="overtime-details">
              <span>Starts after <strong>{{ empData.startOverTime }} hours</strong></span>
              <span v-if="overtimeHours > 0"><strong>{{ secondsToHms(overtimeHours) }}</strong> overtime</span>
              <span>Rate <strong>{{ empData.overTimeRate | currency }}</strong></span>
            </div>
            <div class="pay-lines">
              <div><span>Regular pay</span><strong>{{ regularPay | currency }}</strong></div>
              <div v-if="empData.payOverTime && overtimeHours > 0"><span>Overtime pay</span><strong>{{ overtimePay | currency }}</strong></div>
              <div class="pay-total"><span>Total pay</span><strong>{{ totalPayWithOvertime | currency }}</strong></div>
            </div>
            <div v-if="employeeAccess && employeeAccess.pay.create" class="create-pay-action">
              <CreatePay v-if="showPayBtn && !loading" @submit="savePayment" @error="dataError"
                :workingSeconds="workingSeconds()" :employee="empData" :grandTotal="totalPayWithOvertime"
                :overtimeHours="overtimeHours" :regularHours="regularHours" :regularPay="regularPay"
                :overtimePay="overtimePay" :totalPayWithOvertime="totalPayWithOvertime" :period="dateTime"
                :totalSeconds="totalSeconds" />
              <span v-else-if="!loading" class="paid-period-note"><v-icon small>mdi-check-circle</v-icon>Payment already recorded for this period</span>
            </div>
          </section>
        </template>

        <section class="daily-breakdown">
          <div class="breakdown-heading">
            <div>
              <div class="section-eyebrow">Attendance</div>
              <h2>Daily breakdown</h2>
            </div>
            <span>{{ Object.keys(unGroupList).length }} work days</span>
          </div>
          <v-card flat class="touch-view-container">
            <v-list class="day-list">
              <template v-for="(row, i) in Object.keys(unGroupList)">
                <v-list-item :key="row" class="day-report-card" @click.stop.prevent="openDetail(Object.entries(unGroupList)[i][1])">
                  <v-list-item-content>
                    <div class="day-card-heading">
                      <div>
                        <strong>{{ Object.entries(unGroupList)[i][1].row[0].clockInTime | moment("dddd") }}</strong>
                        <span>{{ row }}</span>
                      </div>
                      <div class="entry-count">
                        {{ Object.entries(unGroupList)[i][1].row.length }}
                        {{ Object.entries(unGroupList)[i][1].row.length === 1 ? "entry" : "entries" }}
                      </div>
                    </div>
                    <v-progress-linear
                      :class="['hours-progress', { 'is-short': isDayShort(Object.entries(unGroupList)[i][1]) }]"
                      :color="barColor(Object.entries(unGroupList)[i][1])"
                      height="8" rounded background-opacity="0.15"
                      :value="duration(Object.entries(unGroupList)[i][1].totalInSeconds, Object.entries(unGroupList)[i][1].metadata)">
                    </v-progress-linear>
                    <div class="day-card-footer">
                      <div class="day-status">
                        <span v-if="employeeAccess && employeeAccess.report.showHours" class="worked-time">
                          <v-icon small>mdi-clock-outline</v-icon>
                          {{ secondsToHms(Object.entries(unGroupList)[i][1].totalInSeconds) }}
                        </span>
                        <span v-if="Object.entries(unGroupList)[i][1].row[0].TimeClockPayId" class="status-badge is-paid">
                          <v-icon small>mdi-check</v-icon>Paid
                        </span>
                        <span v-if="Object.entries(unGroupList)[i][1].astrid" class="status-badge is-open">
                          <v-icon small>mdi-alert-outline</v-icon>Open shift · not counted in pay
                        </span>
                      </div>
                      <span v-if="Object.entries(unGroupList)[i][1].early" class="short-time">
                        {{ secondsToHms(Object.entries(unGroupList)[i][1].early) }} short
                      </span>
                      <v-icon class="row-chevron">mdi-chevron-right</v-icon>
                    </div>
                  </v-list-item-content>
                </v-list-item>
              </template>
              <div v-if="!loading && Object.keys(unGroupList).length === 0" class="empty-report">
                <v-icon>mdi-calendar-blank-outline</v-icon>
                <strong>No time records</strong>
                <span>There are no shifts recorded for this date range.</span>
              </div>
            </v-list>
          </v-card>
        </section>
        <DayDetail @submit="dataSave" @close="dialog = false" :dialog="dialog" :selectedRow="selectedRow" />
      </v-container>
    </v-card>
    <v-dialog v-model="showPdf" width="700">
      <v-card style="height: 800px">
        <v-toolbar class="elevation-0">
          <v-toolbar-title class="subheading">Time Card</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click.stop.prevent="showPdf = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-layout fill-height column>
          <embed id="embedPDF" :src="pdf" type="application/pdf" height="100%" width="100%" />
        </v-layout>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import timeClockService from "@/modules/TimeClock/service";
import DayDetail from "@/modules/Reports/components/DayDetail.vue";
// import employeeService from "@/modules/Employee/service";
import DateTimeTextField from "@/components/DateTimeTextField.vue";
import locationService from "@/modules/Location/service.js";
import DateTimePicker from "@/components/DateTimePicker.vue";
import Create from "@/modules/Reports/components/Create.vue";
import CreatePay from "@/modules/Pay/components/Create.vue";
import authService from "@/modules/Authentication/service.js";
import pdfMake from 'pdfmake/build/pdfmake.min';
import { pdfMake as pdfFonts } from 'pdfmake/build/vfs_fonts';
// import Menu from "@/components/AppBarMenu.vue";

import _ from "lodash";
import { mapGetters } from "vuex";

pdfMake.vfs = pdfFonts.vfs;
export default {
  props: ["EmployeeId", "empData", "dateTime", "mainDialog"],
  data() {
    return {
      detailReportDialog: false,
      reload: false,
      integration: null,
      workingHours: 12,
      locationCode: null,
      showPayBtn: true,
      editDialog: false,
      editRow: null,
      selectedRow: null,
      dialog: false,
      totalSeconds: 0,
      unGroupList: [],
      loading: false,
      selectedEmployees: [],
      employees: [],
      timeClockList: [],
      totalTime: 0,
      count: 0,
      doc: {
        content: []
      },
      location: null,
      pdf: null,
      showPdf: false,
      metadata: {
        customClockIn: {
          storeTimeClockImage: false,
          defaultWorkingHours: 12,
          storeTimeClockImageView: false,
        },
      },
    };
  },
  components: {
    DayDetail,
    DateTimePicker,
    DateTimeTextField,
    CreatePay,
    Create,
  },
  // activated() {
  //   this.locationCode = localStorage.getItem(
  //     `${process.env.VUE_APP_STORAGE_PREFIX}code`
  //   );
  //   if (
  //     this.$route.params.id &&
  //     this.$route.params &&
  //     this.$route.params.dateTime
  //   ) {
  //     const id = this.$route.params.id;
  //     this.EmployeeId = id;
  //     this.empData = this.$route.params.empData;
  //     this.dateTime = this.$route.params.dateTime;
  //     this.loadTimeClock();
  //     // console.log("dateTime", this.$route.params);
  //   } else {
  //     this.$router.push({ name: "hours-summary" });
  //   }
  //   this.loadSetting();
  //   // console.log('this.empData', this.empData)
  // },
  computed: {
    ...mapGetters("employee", ["employeeAccess"]),
    totalWorkedHours() {
      return this.totalSeconds / 3600; // Convert total seconds to hours
    },
    regularHours() {
      // If overtime is NOT enabled, all hours are regular hours
      if (!this.empData.payOverTime) {
        return this.totalWorkedHours;
      }

      // If total hours are less than overtime start time, return total hours worked
      if (this.totalWorkedHours <= this.empData.startOverTime) {
        return this.totalWorkedHours;
      }

      // Otherwise, cap regular hours at startOverTime (default 40)
      return Math.min(this.totalWorkedHours, this.empData.startOverTime);
    },
    overtimeHours() {
      // Ensure overtime applies only if payOverTime is true and total hours exceed startOverTime
      if (!this.empData.payOverTime) {
        return 0; // No overtime if payOverTime is false
      }

      // Calculate overtime in hours first
      const overtimeInHours = Math.max(0, this.totalWorkedHours - this.empData.startOverTime);

      // Convert overtime hours to seconds (1 hour = 3600 seconds)
      return overtimeInHours * 3600;
    },
    regularPay() {
      // Ensure it always returns a value
      console.log('regularHours', this.regularHours)
      return this.regularHours * (this.empData.hourlyRate || 0);
    },
    // overtimePay() {
    //   // Ensure it returns 0 if overtime is not active
    //   return this.empData.payOverTime ? this.overtimeHours * (this.empData.overTimeRate || 0) : 0;
    // },
    overtimePay() {
      // Ensure overtime applies only if payOverTime is true
      if (!this.empData.payOverTime) {
        return 0;
      }

      // Convert overtime rate from hourly to per-second rate
      const overtimeRatePerSecond = (this.empData.overTimeRate || 0) / 3600;

      // Calculate overtime pay based on worked overtime seconds
      return this.overtimeHours * overtimeRatePerSecond;
    },
    totalPayWithOvertime() {
      // Total should always be the sum of regular and overtime pay
      return this.regularPay + this.overtimePay;
    },
    grandTotal_total() {
      if (this.empData.metadata && this.empData.metadata.timeClockLogin && this.empData.metadata.timeClockLogin.fullDayEmployee) {
        const totalHoursByDate = {};
        let totalCash = 0;
        this.timeClockList.forEach((row) => {
          if (row.clockOutTime) {
            const date = this.$moment(row.clockOutTime).format('YYYY-MM-DD');
            const totalHours = parseFloat(row.shiftDuration.totalInHours);
            // Check if the date already exists in the totalHoursByDate object
            if (totalHoursByDate[date]) {
              totalHoursByDate[date] += totalHours;
            } else {
              // If it doesn't exist, create a new entry with the total hours value
              totalHoursByDate[date] = totalHours;
            }
          }
        });
        Object.entries(totalHoursByDate).forEach(([totalHours]) => {
          // console.log("date ", date)
          if (this.empData.metadata.timeClockLogin && totalHours >= parseInt(this.empData.metadata.timeClockLogin.workingHours)) {
            totalCash += this.empData.metadata.timeClockLogin.fullDayRate ? parseFloat(this.empData.metadata.timeClockLogin.fullDayRate) : 0;
          } else if (this.empData.metadata.timeClockLogin && totalHours >= parseInt(this.empData.metadata.timeClockLogin.halfDayHours)) {
            totalCash += this.empData.metadata.timeClockLogin.fullDayRate ? parseFloat(this.empData.metadata.timeClockLogin.fullDayRate) / 2 : 0;
          }
        });
        return totalCash;
      }
      let totalHours = 0;
      this.timeClockList.map((row) => {
        if (row.clockOutTime) {
          totalHours += parseFloat(row.shiftDuration.totalInHours);
        }
      });
      return totalHours * this.empData.hourlyRate;
    },
  },
  created() {
    let localLocation = localStorage.getItem(
      `${process.env.VUE_APP_STORAGE_PREFIX}location`
    );


    if (localLocation) {
      localLocation = JSON.parse(localLocation);

      locationService.Terminal.getById(localLocation.id).then(response => {
        this.location = response.data
        if (this.location && this.location.metadata && this.location.metadata.isLocked) {
          this.$router.push({
            name: "no-subscription",
            params: {
              location: this.Location,
            },
          });
        }
      })
      this.detailReportDialog = true
      this.loadTimeClock();
      this.loadSetting();
    }
  },
  watch: {
    mainDialog(val) {
      this.detailReportDialog = val;
      if (val) {
        this.loadTimeClock();
        this.loadSetting();
      }
    },
    empData(val) {
      // console.log("val emp change", val);
      if (
        val.metadata &&
        val.metadata.timeClockLogin &&
        val.metadata.timeClockLogin.workingHours
      ) {
        // console.log("in here");
        this.workingHours = val.metadata.timeClockLogin.workingHours;
      }
    },
  },
  methods: {
    print() {
      this.showPdf = true
    },
    async createDocument() {
      this.doc.content = [];

      // Location Header
      if (this.location) {
        this.doc.content.push({
          text: this.location.name,
          alignment: 'center',
          fontSize: 15,
        });
      }

      // Time Card Header
      this.doc.content.push({
        text: "Time Card",
        alignment: 'center',
        fontSize: 15,
      });

      // Employee Details
      this.doc.content.push({
        text: this.empData.name,
        alignment: 'left',
        fontSize: 15,
      });
      this.doc.content.push({
        text: this.empData.phoneNumber,
        alignment: 'left',
      });

      // Pay Rate (if applicable)
      if (this.employeeAccess && this.employeeAccess.report.showPay) {
        this.doc.content.push({
          text: 'Rate: $' + this.empData.hourlyRate,
          alignment: 'left',
        });
      }

      // Date Range
      this.doc.content.push({
        text: 'From: ' + this.$moment(this.dateTime.start).format('ddd MM/DD/YYYY'),
        alignment: 'left',
      });
      this.doc.content.push({
        text: 'To: ' + this.$moment(this.dateTime.end).format('ddd MM/DD/YYYY'),
        alignment: 'left',
      });

      // Working Hours
      this.doc.content.push({
        text: 'Daily Working Hours: ' + this.workingHours + ' Hr',
        alignment: 'left',
      });

      // Worked Hours
      if (this.employeeAccess && this.employeeAccess.report.showHours) {
        this.doc.content.push({
          text: 'Worked Hours: ' + this.secondsToHms(this.totalSeconds),
          alignment: 'left',
        });
      }

      // Supposed to Work
      this.doc.content.push({
        text: 'Supposed To Work: ' + this.secondsToHms(this.workingSeconds()),
        alignment: 'left',
      });

      // Check if Overtime is Active
      if (this.empData.payOverTime) {
        this.doc.content.push({
          text: 'Overtime Active',
          alignment: 'left',
          bold: true,
          color: 'blue'
        });

        // Overtime Details
        this.doc.content.push({
          text: 'Overtime Starts After: ' + this.empData.startOverTime + ' Hr',
          alignment: 'left',
        });
        this.doc.content.push({
          text: 'Overtime Hours: ' + this.secondsToHms(this.overtimeHours),
          alignment: 'left',
        });
        this.doc.content.push({
          text: 'Overtime Rate: $' + this.empData.overTimeRate.toFixed(2),
          alignment: 'left',
        });

        // Overtime Pay
        this.doc.content.push({
          text: 'Overtime Pay: $' + this.overtimePay.toFixed(2),
          alignment: 'right',
        });
      }

      // Total Pay
      if (this.employeeAccess && this.employeeAccess.report.showPay) {
        this.doc.content.push({
          text: 'Regular Pay: $' + this.regularPay.toFixed(2),
          alignment: 'right',
        });

        // Show Total Pay (Regular + Overtime)
        this.doc.content.push({
          text: 'Total Pay: $' + this.totalPayWithOvertime.toFixed(2),
          alignment: 'right',
          bold: true,
          fontSize: 14
        });
      }

      // Time Clock Entries
      for (const date in this.unGroupList) {
        if (this.employeeAccess && this.employeeAccess.report.showHours) {
          this.doc.content.push({
            columns: [
              { text: date + ' - ' + this.$moment(date).format('dddd'), alignment: 'left' },
              { text: this.secondsToHms(this.unGroupList[date].totalInSeconds), alignment: 'right' },
            ],
            margin: [0, 20, 0, 5]
          });
        } else {
          this.doc.content.push({
            text: date + ' - ' + this.$moment(date).format('dddd'),
            alignment: 'left',
            margin: [0, 20, 0, 5]
          });
        }

        let timeClockHistory = [['IN', 'OUT', 'WORK']]
        this.unGroupList[date].row.map(item => {
          if (this.employeeAccess && this.employeeAccess.report.showHours) {
            if (item.clockOutTime)
              timeClockHistory.push([
                this.$moment(item.clockInTime).format('h:mm A').toString(),
                this.$moment(item.clockOutTime).format('h:mm A').toString(),
                this.secondsToHms(item.shiftDuration.totalInSeconds).toString()
              ]);
            else
              timeClockHistory.push([
                this.$moment(item.clockInTime).format('h:mm A').toString(),
                '-',
                this.secondsToHms(item.shiftDuration.totalInSeconds).toString()
              ]);
          } else {
            if (item.clockOutTime)
              timeClockHistory.push([
                this.$moment(item.clockInTime).format('h:mm A').toString(),
                this.$moment(item.clockOutTime).format('h:mm A').toString(),
                '-'
              ]);
            else
              timeClockHistory.push([
                this.$moment(item.clockInTime).format('h:mm A').toString(),
                '-',
                '-'
              ]);
          }
        });

        this.doc.content.push({
          table: {
            widths: [100, 100, '*'],
            body: timeClockHistory
          },
        });
      }

      // Generate PDF
      pdfMake.createPdf(this.doc).getDataUrl(data => {
        this.pdf = data;
      });
    },
    // async createDocument() {
    //   this.doc.content = []
    //   if (this.location) {
    //     this.doc.content.push({
    //       text: this.location.name,
    //       alignment: 'center',
    //       fontSize: 15,
    //     });
    //   }
    //   this.doc.content.push({
    //     text: "Time Card",
    //     alignment: 'center',
    //     fontSize: 15,
    //   });
    //   this.doc.content.push({
    //     text: this.empData.name,
    //     alignment: 'left',
    //     fontSize: 15,
    //   });
    //   this.doc.content.push({
    //     text: this.empData.phoneNumber,
    //     alignment: 'left',
    //   });
    //   if (this.employeeAccess && this.employeeAccess.report.showPay) {
    //     this.doc.content.push({
    //       text: 'Rate: $' + this.empData.hourlyRate,
    //       alignment: 'left',
    //     });
    //   }
    //   this.doc.content.push({
    //     text: 'From: ' + this.$moment(this.dateTime.start).format('ddd MM/DD/YYYY'),
    //     alignment: 'left',
    //   });
    //   this.doc.content.push({
    //     text: 'To: ' + this.$moment(this.dateTime.end).format('ddd MM/DD/YYYY'),
    //     alignment: 'left',
    //   });
    //   this.doc.content.push({
    //     text: 'Daily Working Hours: ' + this.workingHours + 'Hr',
    //     alignment: 'left',
    //   });
    //   if (this.employeeAccess && this.employeeAccess.report.showHours) {
    //     this.doc.content.push({
    //       text: 'Worked Hours ' + this.secondsToHms(this.totalSeconds),
    //       alignment: 'left',
    //     });
    //   }
    //   this.doc.content.push({
    //     text: 'Supposed To Work ' + this.secondsToHms(this.workingSeconds()),
    //     alignment: 'left',
    //   });
    //   if (this.employeeAccess && this.employeeAccess.report.showPay) {
    //     this.doc.content.push({
    //       text: 'Total Pay: $' + this.grandTotal_total.toFixed(2),
    //       alignment: 'right',
    //     });
    //   }
    //   for (const date in this.unGroupList) {
    //     if (this.employeeAccess && this.employeeAccess.report.showHours) {
    //       this.doc.content.push({
    //         columns: [
    //           { text: date + ' - ' + this.$moment(date).format('dddd'), alignment: 'left' },
    //           { text: this.secondsToHms(this.unGroupList[date].totalInSeconds), alignment: 'right' },
    //         ],
    //         margin: [0, 20, 0, 5]
    //       });
    //     } else {
    //       this.doc.content.push({
    //         text: date + ' - ' + this.$moment(date).format('dddd'),
    //         alignment: 'left',
    //         margin: [0, 20, 0, 5]
    //       });
    //     }
    //     let timeClockHistory = [['IN', 'OUT', 'WORK']]
    //     this.unGroupList[date].row.map(item => {
    //       if (this.employeeAccess && this.employeeAccess.report.showHours) {
    //         if (item.clockOutTime)
    //           timeClockHistory.push([this.$moment(item.clockInTime).format('h:mm A').toString(), this.$moment(item.clockOutTime).format('h:mm A').toString(), this.secondsToHms(item.shiftDuration.totalInSeconds).toString()])
    //         else
    //           timeClockHistory.push([this.$moment(item.clockInTime).format('h:mm A').toString(), '-', this.secondsToHms(item.shiftDuration.totalInSeconds).toString()])
    //       } else {
    //         if (item.clockOutTime)
    //           timeClockHistory.push([this.$moment(item.clockInTime).format('h:mm A').toString(), this.$moment(item.clockOutTime).format('h:mm A').toString(), '-'])
    //         else
    //           timeClockHistory.push([this.$moment(item.clockInTime).format('h:mm A').toString(), '-', '-'])
    //       }
    //     })
    //     this.doc.content.push(
    //       {
    //         table: {
    //           widths: [100, 100, '*'],
    //           body: timeClockHistory
    //         },
    //       },
    //     )
    //   }
    //   pdfMake.createPdf(this.doc).getDataUrl(data => {
    //     this.pdf = data;
    //   });
    // },
    backToReport() {
      this.$router.push({
        name: "hours-summary",
        params: { reload: this.reload },
      });
    },
    loadSetting() {
      authService.getLocation(this.locationCode).then((response) => {
        // console.log("response", response);
        response.data.Integrations.map((row) => {
          if (row.pubSubHook === "TERMINALTIMECLOCK") {
            this.integration = row;
            // console.log(row);
          }
        });
        if (response.data.metadata && response.data.metadata.customClockIn) {
          // console.log("in custom close in ");

          this.metadata = {
            ...this.metadata,
            ...response.data.metadata,
          };
        } else {
          if (this.integration) {
            this.metadata = {
              ...this.metadata,
              ...this.integration.LocationIntegration.metadata,
            };
          }
        }

        if (
          this.metadata.customClockIn &&
          this.metadata.customClockIn.defaultWorkingHours
        ) {
          this.workingHours = this.metadata.customClockIn.defaultWorkingHours;
        }
        // console.log(
        //   "this.integration.LocationIntegration.metadata",
        //   this.integration.LocationIntegration.metadata
        // );

        if (
          this.empData.metadata &&
          this.empData.metadata.timeClockLogin &&
          this.empData.metadata.timeClockLogin.workingHours
        ) {
          this.workingHours = this.empData.metadata.timeClockLogin.workingHours;
        }
        // this.metadata = {
        //   ...this.metadata,
        //   ...this.integration.LocationIntegration.metadata,
        // };
        // this.checkStatus();
      });
    },
    barColor(obj) {
      if (obj.astrid) {
        return "#4f9cf9";
      }
      return this.isDayShort(obj) ? "#ef4d5a" : "#35b978";
    },
    isDayShort(obj) {
      const expectedSeconds = Number(this.workingHours) * 60 * 60;
      const workedSeconds = Number(obj.totalInSeconds) || 0;
      const isShort = expectedSeconds > 0 && workedSeconds < expectedSeconds;
      if (!obj.astrid) {
        this.$set(obj, "early", isShort ? expectedSeconds - workedSeconds : 0);
      }
      return !obj.astrid && isShort;
    },
    dataSave() {
      // this.snackbar.text = "Time entry saved";
      // this.snackbar.status = true;
      // this.snackbar.color = "black";
      // this.$toast.success("Time entry saved", { timeout: 2000 });
      this.reload = true;
      this.loadTimeClock();
      this.dialog = false;
    },
    savePayment() {
      this.$toast.success("Pay created!", { timeout: 2000 });
      this.loadTimeClock();
    },
    dataError(e) {
      console.log("errdataErrordataErroror", e);
      this.snackbar.text = "Can't save entry, check date and time";
      this.snackbar.status = true;
      this.snackbar.color = "error";
    },
    openDetail(row) {
      this.selectedRow = row;
      this.reload = true;
      this.dialog = true;
    },
    diffTime() {
      let value = this.workingSeconds() - this.totalSeconds;
      return value;
    },
    workingSeconds() {
      let value = this.workingHours;
      // if (
      //   this.empData.metadata &&
      //   this.empData.metadata.timeClockLogin &&
      //   this.empData.metadata.timeClockLogin.workingHours
      // ) {
      //   value = this.empData.metadata.timeClockLogin.workingHours;
      // }
      value *= Object.keys(this.unGroupList).length;
      // empData.metadata && empData.metadata.timeClockLogin && empData.metadata.timeClockLogin.workingHours
      let seconds = value * 60 * 60;
      return seconds;
    },
    secondsToHms(d) {
      d = Number(d);
      var h = Math.floor(d / 3600);
      var m = Math.floor((d % 3600) / 60);
      var s = Math.floor((d % 3600) % 60);

      // console.log('AndDown', this.$vuetify.breakpoint.xsOnly)

      var hDisplay = h > 0 ? h + (h == 1 ? " hour, " : " hours, ") : "";
      var mDisplay = m > 0 ? m + (m == 1 ? " minute, " : " minutes, ") : "";
      var sDisplay = s > 0 ? s + (s == 1 ? " second" : " seconds") : "";

      if (this.$vuetify.breakpoint.xsOnly) {
        hDisplay = h > 0 ? h + " h, " : "";
        mDisplay = m > 0 ? m + " m, " : "";
        sDisplay = s > 0 ? s + " s " : "";
      }

      return hDisplay + mDisplay + sDisplay;
    },
    duration(seconds) {
      let value = 10;
      // let workingHours = 12;

      // if (
      //   metadata &&
      //   metadata.timeClockLogin &&
      //   metadata.timeClockLogin.workingHours
      // ) {
      //   workingHours = metadata.timeClockLogin.workingHours;
      // }

      const totalTime = this.workingHours * 60 * 60;
      // console.log('row', row, totalTime)
      value = (seconds / totalTime) * 100;
      return value;
    },
    loadTimeClock() {
      this.loading = true;
      this.showPayBtn = true;
      const dataObj = {
        start: this.dateTime.start,
        end: this.dateTime.end,
        EmployeeId: this.EmployeeId,
      };
      // console.log("dataObj", dataObj);
      return timeClockService
        .getEmployeeTimeClockReport(dataObj)
        .then((response) => {
          // console.log("loadTimeClock", response);
          this.totalSeconds = 0;
          this.unGroupList = _.mapValues(
            _.groupBy(response.data.timeClockList, (row) => {
              // console.log("row.TimeClockPayId", row.TimeClockPayId);
              if (row.TimeClockPayId) {
                this.showPayBtn = false;
              }
              return this.$moment(row.clockInTime).format("MM/DD/YYYY");
            }),
            (list) => {
              // console.log('printing list', list)
              // let newlist = []
              if (list.length > 1) {
                let seconds = 0;
                let astrid = false;
                list.map((row) => {
                  if (row.clockOutTime) {
                    seconds += row.shiftDuration.totalInSeconds;
                    this.totalSeconds += row.shiftDuration.totalInSeconds;
                  } else {
                    astrid = true;
                  }
                });
                // newlist.push({
                //     createdAt: list[0].createdAt,
                //     totalInSeconds: seconds
                // })
                return {
                  clockInTime: list[0].clockInTime,
                  astrid,
                  totalInSeconds: seconds,
                  metadata: list[0].Employee.metadata,
                  row: list,
                };
              } else {
                this.totalSeconds += list[0].shiftDuration.totalInSeconds;
                return {
                  id: list[0].id,
                  clockInTime: list[0].clockInTime,
                  astrid: list[0].clockOutTime ? false : true,
                  totalInSeconds: list[0].shiftDuration.totalInSeconds,
                  metadata: list[0].Employee.metadata,
                  row: list,
                };
              }
            }
          );

          this.timeClockList = response.data.timeClockList;
          this.totalTime = response.data.totalTime;
          this.count = this.timeClockList.length;
          if (!this.empData) {
            if (this.timeClockList.length > 1) {
              this.empData = this.timeClockList[0].Employee;
            }
          }
          this.createDocument()
          // console.log("this.unGroupList", this.unGroupList);
          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
          console.log("error is :", error);
        });
    },
  },
};
</script>

<style scoped>
.detail-report-bar {
  background: var(--v-surface-base) !important;
  border-bottom: 1px solid var(--v-border-base);
  color: var(--v-textPrimary-base) !important;
}

.close-button {
  background: var(--v-surfaceMuted-base);
  margin-right: 12px;
}

.report-title {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
  min-width: 0;
}

.report-title strong {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.report-eyebrow,
.section-eyebrow {
  color: var(--v-textSecondary-base);
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.report-action {
  border-color: var(--v-border-base);
  color: var(--v-textPrimary-base);
  margin-right: 8px;
}

.detail-report-screen {
  background: var(--v-background-base) !important;
  color: var(--v-textPrimary-base);
  min-height: calc(100vh - 64px);
}

.report-loader {
  position: fixed;
  z-index: 5;
}

.detail-report-container {
  max-width: 1180px;
  padding: 28px 24px 40px;
}

.employee-overview,
.pay-overview,
.daily-breakdown {
  background: var(--v-surface-base);
  border: 1px solid var(--v-border-base);
  border-radius: 20px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
  padding: 24px;
}

.employee-heading,
.pay-heading,
.breakdown-heading,
.day-card-heading,
.day-card-footer {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.employee-heading {
  justify-content: flex-start;
  margin-bottom: 22px;
}

.employee-avatar {
  align-items: center;
  background: rgba(37, 194, 214, 0.14);
  border: 1px solid rgba(37, 194, 214, 0.35);
  border-radius: 16px;
  color: var(--v-primary-base);
  display: flex;
  flex: 0 0 56px;
  font-size: 1.4rem;
  font-weight: 800;
  height: 56px;
  justify-content: center;
  margin-right: 16px;
}

.employee-name,
.pay-heading h2,
.breakdown-heading h2 {
  color: var(--v-textPrimary-base);
  line-height: 1.2;
  margin: 2px 0 0;
}

.employee-name {
  font-size: 1.55rem;
}

.employee-meta {
  color: var(--v-textSecondary-base);
  display: flex;
  flex-wrap: wrap;
  font-size: 0.82rem;
  gap: 6px 18px;
  margin-top: 6px;
}

.employee-meta span {
  align-items: center;
  display: flex;
  gap: 5px;
}

.employee-meta .v-icon {
  color: var(--v-textSecondary-base);
}

.metric-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.metric-card {
  background: var(--v-surfaceMuted-base);
  border: 1px solid var(--v-border-base);
  border-radius: 14px;
  min-height: 90px;
  padding: 16px;
}

.metric-label {
  color: var(--v-textSecondary-base);
  display: block;
  font-size: 0.75rem;
  font-weight: 700;
  margin-bottom: 8px;
}

.metric-card strong {
  color: var(--v-textPrimary-base);
  display: block;
  font-size: 1.05rem;
  line-height: 1.3;
}

.metric-card small {
  color: var(--v-textSecondary-base);
  font-size: 0.72rem;
  font-weight: 600;
}

.variance-card {
  align-items: center;
  border-radius: 14px;
  display: flex;
  gap: 12px;
  margin-top: 12px;
  padding: 12px 16px;
}

.variance-card.is-short {
  background: rgba(244, 83, 94, 0.11);
  color: #f36b75;
}

.variance-card.is-over {
  background: rgba(45, 190, 126, 0.11);
  color: #31c989;
}

.variance-card .v-icon {
  color: inherit;
}

.variance-card span,
.variance-card strong {
  display: block;
}

.variance-card span {
  font-size: 0.75rem;
  opacity: 0.9;
}

.pay-heading {
  margin-bottom: 18px;
}

.overtime-chip {
  background: rgba(72, 151, 255, 0.14) !important;
  color: #68a9ff !important;
}

.overtime-details {
  background: var(--v-surfaceMuted-base);
  border-radius: 12px;
  color: var(--v-textSecondary-base);
  display: flex;
  flex-wrap: wrap;
  font-size: 0.8rem;
  gap: 8px 24px;
  margin-bottom: 10px;
  padding: 12px 14px;
}

.pay-lines > div {
  align-items: center;
  border-bottom: 1px solid var(--v-border-base);
  color: var(--v-textSecondary-base);
  display: flex;
  justify-content: space-between;
  padding: 11px 0;
}

.pay-lines strong {
  color: var(--v-textPrimary-base);
}

.pay-lines .pay-total {
  border-bottom: 0;
  color: var(--v-textPrimary-base);
  font-size: 1.15rem;
  font-weight: 800;
}

.pay-total strong {
  color: var(--v-primary-base);
  font-size: 1.45rem;
}

.create-pay-action {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
}

.paid-period-note {
  align-items: center;
  color: #31c989;
  display: flex;
  font-size: 0.8rem;
  gap: 5px;
}

.paid-period-note .v-icon {
  color: inherit;
}

.breakdown-heading {
  margin-bottom: 16px;
}

.breakdown-heading > span {
  color: var(--v-textSecondary-base);
  font-size: 0.8rem;
  font-weight: 700;
}

.touch-view-container {
  background: transparent !important;
  border: 0;
  height: auto;
  max-height: none;
  width: 100%;
}

.day-list {
  background: transparent !important;
  padding: 0;
}

.day-report-card {
  background: var(--v-surfaceMuted-base);
  border: 1px solid var(--v-border-base);
  border-radius: 14px;
  margin-bottom: 10px;
  min-height: 112px;
  padding: 12px 14px;
  transition: border-color 0.2s, transform 0.2s;
}

.day-report-card:hover {
  border-color: var(--v-primary-base);
  transform: translateY(-1px);
}

.day-card-heading strong,
.day-card-heading span {
  display: block;
}

.day-card-heading strong {
  color: var(--v-textPrimary-base);
  font-size: 0.95rem;
}

.day-card-heading span,
.entry-count {
  color: var(--v-textSecondary-base);
  font-size: 0.75rem;
}

.entry-count {
  background: var(--v-surface-base);
  border: 1px solid var(--v-border-base);
  border-radius: 999px;
  padding: 5px 10px;
}

.hours-progress {
  margin: 13px 0 11px;
}

.hours-progress.is-short {
  background-color: rgba(239, 77, 90, 0.14) !important;
}

.day-card-footer {
  min-height: 24px;
}

.day-status,
.worked-time,
.status-badge {
  align-items: center;
  display: flex;
}

.day-status {
  flex-wrap: wrap;
  gap: 7px;
}

.worked-time {
  color: var(--v-textPrimary-base);
  font-size: 0.8rem;
  font-weight: 600;
  gap: 5px;
}

.worked-time .v-icon {
  color: var(--v-textSecondary-base);
}

.status-badge {
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 800;
  gap: 3px;
  padding: 3px 8px;
}

.status-badge.is-paid {
  background: rgba(45, 190, 126, 0.12);
  color: #31c989;
}

.status-badge.is-open,
.short-time {
  color: #f36b75;
}

.status-badge.is-open {
  background: rgba(244, 83, 94, 0.11);
}

.status-badge .v-icon {
  color: inherit;
}

.short-time {
  font-size: 0.75rem;
  font-weight: 700;
  margin-left: auto;
}

.row-chevron {
  color: var(--v-textSecondary-base);
  margin-left: 10px;
}

.empty-report {
  align-items: center;
  color: var(--v-textSecondary-base);
  display: flex;
  flex-direction: column;
  gap: 7px;
  padding: 40px 16px;
  text-align: center;
}

.empty-report .v-icon {
  color: var(--v-textSecondary-base);
  font-size: 36px;
}

.empty-report strong {
  color: var(--v-textPrimary-base);
}

@media (max-width: 760px) {
  .detail-report-bar {
    padding: 0 4px;
  }

  .report-title {
    font-size: 0.92rem;
  }

  .report-eyebrow {
    font-size: 0.58rem;
  }

  .action-label {
    display: none;
  }

  .report-action {
    min-width: 40px !important;
    padding: 0 10px !important;
  }

  .report-action .v-icon {
    margin: 0 !important;
  }

  .detail-report-container {
    padding: 14px 12px 28px;
  }

  .employee-overview,
  .pay-overview,
  .daily-breakdown {
    border-radius: 16px;
    margin-bottom: 12px;
    padding: 16px;
  }

  .employee-avatar {
    border-radius: 13px;
    flex-basis: 46px;
    height: 46px;
  }

  .employee-name {
    font-size: 1.15rem;
  }

  .employee-meta {
    display: block;
  }

  .employee-meta span {
    margin-top: 4px;
  }

  .metric-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .metric-card {
    min-height: 82px;
    padding: 13px;
  }

  .metric-card strong {
    font-size: 0.9rem;
  }

  .overtime-details {
    display: grid;
  }

  .day-report-card {
    padding: 10px;
  }

  .day-card-footer {
    align-items: center;
    display: grid;
    gap: 8px;
    grid-template-columns: minmax(0, 1fr) auto auto;
  }

  .day-status {
    min-width: 0;
  }

  .worked-time {
    line-height: 1.4;
    white-space: normal;
  }

  .short-time {
    grid-column: 2;
    grid-row: 1;
    line-height: 1.4;
    margin-left: auto;
    text-align: right;
    white-space: nowrap;
    width: auto;
  }

  .row-chevron {
    align-self: center;
    grid-column: 3;
    grid-row: 1;
    margin-left: 2px;
  }
}

@media (max-width: 390px) {
  .short-time {
    font-size: 0.68rem;
  }
}

@media (max-width: 430px) {
  .close-button {
    margin-right: 4px;
  }

  .report-title {
    max-width: 132px;
  }

  .pay-heading {
    align-items: flex-start;
    gap: 8px;
  }

  .overtime-chip {
    margin: 0;
  }
}
</style>
